// THE BLACK COURT: a living chess army of sixteen riveted chess-piece robots on a board that erupts from
// the boss island (Godot scripts/enemies/chess_board.gd + chess_king.gd + chess_rook.gd,
// docs/BOSS_GRAMMAR.md §5-6, models from tools/boss_forge/black_court.py).
// Server-only brain; every piece is an ordinary enemy (EType.CourtKing..CourtPawn) so grenades,
// rockets, melee, specials, prime/detonate, burn and lag compensation all reach them.
//
// Read surface: the board and the court's roles. Core verb: breakthrough and assassination.
// Windows come from piercing the line and flanking the King.
//   THE KING (the win condition) is AP5 everywhere except the GOLD amplifier on his back. He always
//   turns to face the nearest cowboy (finitely: a committed strafe rounds him) and backs away from
//   anyone inside 13 m. Every ~6.5 s he panics and SCUTTLES: back turned, bolting for 1.7 s (the
//   guaranteed back window, even solo). Close on him and he SPIN-STRIKES: a 0.8 s wind-up, a whip
//   that shoves, then he stands spent with his back to you.
//   THE COURT moves in turns (G1 at army altitude): on every TOCK of the court clock each piece
//   makes one chess-flavoured move (pawns one rank forward, rooks along files and ranks, bishops on
//   diagonals, knights in L-hops, the queen any way), then the court holds. One major action at a
//   time, each TELL → EXECUTE → RECOVER:
//     ROOK CHARGE    a rook lined up on a cowboy's file or rank rears and rams the lane
//     MITRE BEAMS    a bishop flares both diagonals through its square, then they burn
//     KNIGHT LEAP    a knight crouches over a ring locked on a cowboy and lands on it
//     ROYAL DECREE   the super: the queen marks all eight of her lines (ranks, files, diagonals)
//                    for 1.3 s, then they detonate. Stand between the lines.
//     SPIN STRIKE    the King's own (see above)
//   Pawns peck with slow bolts on the tock (minor pressure; it holds during every TELL / RECOVER).
//   One ROOK at a time DEFENDS: it stops attacking and interposes between the King and the nearest
//   cowboy, a wall of boiler plate; the rooks swap duty every 11 s.
//   While the King lives, a fallen piece RE-FORMS on its home square 9 s later, the square glowing
//   for the last 1.5 s as it rises (G5): you cannot out-shoot the court. Kill the King and it is
//   CHECKMATE: the whole court falls with him.
// Liberties against the Godot fight: no 100-HP seal dome before the match (the board erupting and
// the court rising is the opener); no royal aegis shield (the scuttle window and the back core
// carry the fight); pieces move on the board's grid in chess shapes rather than with their stock
// robot movesets (the "court moves in turns" grammar item, made literal).

import { DT, clamp, len2, lerp, secs, wrapAngle } from '../core';
import type { Reader, Writer } from '../codec';
import { Ev } from '../codec';
import { EType } from '../defs';
import { BKind, BULLETS } from '../bullets';
import { EState, Enemy, historyAt, stepStatus } from '../enemies';
import { PlayerState, isTargetable } from '../player';
import { NO_GROUND } from '../world';
import type { Game } from '../game';
import { BossBase, BossBeat, BossDef, BossHit, BossKind, BossNet, Ray } from './types';

// ------------------------------------------------------------------ the board
/** square size (m); the board is 8 × 8 squares, centred on the arena */
export const SQ = 3;
/** the grid pieces may stand on: the board plus a two-square apron each way */
export const GRID_MIN = -2, GRID_MAX = 9;
/** the King never strays further than this from the board centre (Godot: square × 4.2) */
export const KING_R = 12.6;
export const SLOTS = 16;

export const enum Pc { King = 0, Queen = 1, Rook = 2, Bishop = 3, Knight = 4, Pawn = 5 }
export const PIECE_NAMES = ['KING', 'QUEEN', 'ROOK', 'BISHOP', 'KNIGHT', 'PAWN'];
/** the body enemy type of each piece (EType.CourtKing + piece) */
export const pieceType = (pc: Pc) => (EType.CourtKing + pc) as EType;
export const pieceOf = (t: number) => (t - EType.CourtKing) as Pc;
export const COURT_TYPES: readonly EType[] = [EType.CourtKing, EType.CourtQueen, EType.CourtRook, EType.CourtBishop, EType.CourtKnight, EType.CourtPawn];
/** back rank by file (the King on file 4), then eight pawns */
const BACK_RANK: Pc[] = [Pc.Rook, Pc.Knight, Pc.Bishop, Pc.Queen, Pc.King, Pc.Bishop, Pc.Knight, Pc.Rook];
export const slotPiece = (i: number): Pc => (i < 8 ? BACK_RANK[i] : Pc.Pawn);
const homeF = (i: number) => i % 8;
const homeR = (i: number) => (i < 8 ? 0 : 1);
const KING_SLOT = 4;
const ROOK_SLOTS = [0, 7];

/** solo hull per piece (scaled per posse member and run tier at spawn) */
export const PIECE_HP = [1500, 420, 260, 130, 110, 60];

// ------------------------------------------------------------------ tuning
const RESPAWN = 9, RISE = 1.5;
const INTRO_SECS = 3.2;
const TOCK = [1.8, 1.25]; // court clock: calm → frenzied
const MOVE = 0.5, HOP = 0.62;
const TURN = 3, KING_TURN = 1.3;
const PANIC = 13, KING_WALK = 3.2;
const SCUTTLE_EVERY = 6.5, SCUTTLE_SECS = 1.7, SCUTTLE_SPEED = 5.6;
const SWAP_SECS = 11;
const QUEEN_CD = 13;

/** major actions (Ev.Boss Attack a = this; it is the TELL) */
export const enum Atk { None = 0, Charge = 1, Mitre = 2, Leap = 3, Decree = 4, Spin = 5 }
export const ATTACK_NAMES = ['', 'ROOK CHARGE', 'MITRE BEAMS', 'KNIGHT LEAP', 'ROYAL DECREE', 'SPIN STRIKE'];
/** tell / execute / recover seconds per action (G2 floors: ranged ≥ 0.6, melee/AoE ≥ 0.8, super ≥ 1.2; G3 recover ≥ 0.9) */
export const ATK_SECS: readonly (readonly [number, number, number])[] = [
  [0, 0, 0], [0.9, 1.2, 1.2], [0.9, 0.25, 1.0], [0.8, 0.7, 1.0], [1.3, 0.3, 1.6], [0.8, 0.3, 0.9],
];
export const enum Stage { None = 0, Tell = 1, Exec = 2, Recover = 3 }
const CHARGE_SPEED = 15, CHARGE_W = 1.5, CHARGE_DMG = 24, CHARGE_MAX = 18;
const MITRE_LEN = 16, MITRE_W = 1.0, MITRE_DMG = 20;
const LEAP_R = 3.0, LEAP_DMG = 26, LEAP_H = 8;
const DECREE_LEN = 17, DECREE_W = 1.2, DECREE_DMG = 45;
const SPIN_R = 4.0, SPIN_DMG = 14, SPIN_START = 3.4;
const KNOCK = 15;

/** net.phase bits */
export const enum CourtBit { Intro = 1, Scuttle = 2, Mate = 4, Defender0 = 8, Defender7 = 16 }
/** this boss's own Ev.Boss beats */
export const enum CourtEv { Tock = 16, Reform = 17, Scuttle = 18, Impact = 19, Mate = 20, Swap = 21 }

// ------------------------------------------------------------------ hit volumes
// Boxes in each piece's model space (metres, feet at 0, the piece faces +Z), measured from the named
// nodes of Assets/bosses/chess_*/chess_*.glb (black_court.py). The renderer draws them at scale 1.
type Box = readonly [number, number, number, number, number, number]; // x0 y0 z0 x1 y1 z1
export const enum CPart { None = 0, Chassis = 1, Crown = 2, Core = 3, Body = 4, Head = 5, Weak = 6, Legs = 7 }
interface Vol { box: Box; part: CPart; av: number; mul: number; weak: boolean }
const V = (box: Box, part: CPart, av: number, mul = 1, weak = false): Vol => ({ box, part, av, mul, weak });
export const PIECE_VOLS: readonly (readonly Vol[])[] = [
  // KING: wp_back_core (the gold lens in his rear door's porthole) is the gold path; the tall bell foot,
  // egg torso, floor-length robe panels, the one oversized pauldron (his left) + the small one, arms and the
  // sphere head + crown are the AP5 chassis
  [
    V([-0.4, 2.58, -1.36, 0.4, 3.32, -1.0], CPart.Core, 0, 1.25, true),
    V([-0.72, 4.27, -0.66, 0.72, 6.18, 0.66], CPart.Crown, 4, 1),
    V([-1.32, 2.58, -1.12, 1.32, 4.42, 1.12], CPart.Chassis, 4, 1),
    V([-1.95, 0.09, -2.0, -0.42, 4.14, -0.3], CPart.Chassis, 4, 1),
    V([0.42, 0.09, -2.0, 1.95, 4.14, -0.3], CPart.Chassis, 4, 1),
    V([-2.26, 3.3, -1.0, -0.3, 4.82, 1.0], CPart.Chassis, 4, 1),
    V([0.6, 3.65, -0.55, 1.9, 4.35, 0.55], CPart.Chassis, 4, 1),
    V([-1.85, 1.38, -0.3, 1.85, 4.0, 0.65], CPart.Chassis, 4, 1),
    V([-1.96, 0, -1.96, 1.96, 2.6, 1.96], CPart.Chassis, 4, 1),
  ],
  // QUEEN: the small gold core in her chest porthole (fx_core) is weak; head + coronet, neck, torso + mantle and
  // arms AV2; the bell foot is bare (it was her legs)
  [
    V([-0.2, 1.52, 0.5, 0.2, 1.92, 0.72], CPart.Weak, 0, 1.25, true),
    V([-0.42, 3.0, -0.42, 0.42, 4.08, 0.42], CPart.Head, 2, 1),
    V([-0.24, 2.6, -0.24, 0.24, 3.02, 0.24], CPart.Head, 2, 1),
    V([-0.66, 1.0, -0.6, 0.66, 2.12, 0.6], CPart.Body, 2, 1),
    V([-0.8, 2.1, -0.72, 0.8, 2.68, 0.72], CPart.Body, 2, 1),
    V([-1.0, 0.76, -0.2, 1.0, 2.34, 0.45], CPart.Body, 2, 1),
    V([-0.99, 0, -0.99, 0.99, 1.0, 0.99], CPart.Legs, 0, 1),
  ],
  // ROOK: the cyan slit in the battlement turret is a crit; tower, turret and fists AV2; the bell foot bare
  [
    V([-0.45, 2.26, 0.78, 0.45, 2.42, 0.94], CPart.Weak, 0, 1.25, true),
    V([-0.88, 1.96, -0.88, 0.88, 2.95, 0.88], CPart.Head, 2, 1),
    V([-0.75, 0.82, -0.75, 0.75, 1.98, 0.75], CPart.Body, 2, 1),
    V([-1.3, 0.24, -0.33, 1.3, 2.11, 0.49], CPart.Body, 2, 1),
    V([-1.01, 0, -1.01, 1.01, 0.82, 1.01], CPart.Legs, 0, 1),
  ],
  // BISHOP: the cyan slit under the mitre is a crit; onion body, mitre and arms AV1; the bell foot bare
  [
    V([-0.3, 1.88, 0.32, 0.3, 2.0, 0.47], CPart.Weak, 0, 1.25, true),
    V([-0.6, 0.72, -0.6, 0.6, 2.64, 0.6], CPart.Body, 1, 1),
    V([-0.9, 0.24, -0.15, 0.9, 1.5, 0.38], CPart.Body, 1, 1),
    V([-0.75, 0, -0.75, 0.75, 0.75, 0.75], CPart.Legs, 0, 1),
  ],
  // KNIGHT: the cyan cheek slits of the horse head (turned 35 deg to his right, a chess profile) are a crit;
  // the head AV1, the bell foot bare
  [
    V([-0.5, 1.74, 0.0, 0.05, 1.92, 0.45], CPart.Weak, 0, 1.25, true),
    V([-0.63, 0.84, -0.67, 0.44, 2.26, 0.67], CPart.Head, 1, 1),
    V([-0.77, 0, -0.77, 0.77, 0.85, 0.77], CPart.Legs, 0, 1),
  ],
  // PAWN: the cyan visor of the ball head is a crit; the rest AV0
  [
    V([-0.3, 1.1, 0.25, 0.3, 1.45, 0.48], CPart.Weak, 0, 1.25, true),
    V([-0.44, 0.86, -0.44, 0.44, 1.85, 0.45], CPart.Head, 0, 1),
    V([-0.62, 0, -0.62, 0.62, 0.93, 0.62], CPart.Body, 0, 1),
  ],
];
/** broad-phase sphere per piece: centre height, radius */
const BROAD: readonly (readonly [number, number])[] = [[3.1, 4.3], [2.05, 2.5], [1.5, 2.1], [1.35, 1.75], [1.2, 1.45], [1.0, 1.25]];

/** Ray against one piece at (x, y, z, yaw). Fills the winning volume (or vol = null). */
export function pieceRaycast(
  pc: Pc, x: number, y: number, z: number, yaw: number,
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number,
  out: { t: number; vol: Vol | null },
) {
  out.t = -1; out.vol = null;
  const [by, br] = BROAD[pc];
  {
    const cx = x - ox, cy = y + by - oy, cz = z - oz;
    const b = cx * dx + cy * dy + cz * dz;
    const c = cx * cx + cy * cy + cz * cz - br * br;
    if (c > 0 && (b < 0 || b * b < c)) return out;
  }
  // into the model frame (model +Z = forward; the group yaw is yaw + π like every boss)
  const c = Math.cos(yaw + Math.PI), s = Math.sin(yaw + Math.PI);
  const px = ox - x, pz = oz - z;
  const lox = c * px - s * pz, loz = s * px + c * pz, loy = oy - y;
  const ldx = c * dx - s * dz, ldz = s * dx + c * dz;
  let best = maxT;
  for (const v of PIECE_VOLS[pc]) {
    const b = v.box;
    const t = rayBox(lox, loy, loz, ldx, dy, ldz, b[0], b[1], b[2], b[3], b[4], b[5]);
    const bias = v.weak ? 0.06 : 0;
    if (t >= 0 && t - bias < best) { best = t - bias; out.t = t; out.vol = v; }
  }
  return out;
}

function rayBox(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  let tmin = 0, tmax = 1e9;
  const ax = [ox, oy, oz], ad = [dx, dy, dz], lo = [x0, y0, z0], hi = [x1, y1, z1];
  for (let k = 0; k < 3; k++) {
    if (Math.abs(ad[k]) < 1e-9) { if (ax[k] < lo[k] || ax[k] > hi[k]) return -1; continue; }
    let t1 = (lo[k] - ax[k]) / ad[k], t2 = (hi[k] - ax[k]) / ad[k];
    if (t1 > t2) { const q = t1; t1 = t2; t2 = q; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  return tmin;
}

// ------------------------------------------------------------------ board geometry (shared with the client)
export interface Board { bx: number; bz: number; byaw: number }
/** board-local (file, rank) (fractional ok) → world; rank grows toward the posse */
export function cellWorld(b: Board, f: number, r: number, out: { x: number; z: number }) {
  const lx = (f - 3.5) * SQ, lz = (r - 3.5) * SQ;
  const fx = -Math.sin(b.byaw), fz = -Math.cos(b.byaw);
  const rx = Math.cos(b.byaw), rz = -Math.sin(b.byaw);
  out.x = b.bx + rx * lx + fx * lz; out.z = b.bz + rz * lx + fz * lz;
  return out;
}
/** world → fractional (file, rank) */
export function worldCell(b: Board, x: number, z: number, out: { f: number; r: number }) {
  const dx = x - b.bx, dz = z - b.bz;
  const fx = -Math.sin(b.byaw), fz = -Math.cos(b.byaw);
  const rx = Math.cos(b.byaw), rz = -Math.sin(b.byaw);
  out.f = (dx * rx + dz * rz) / SQ + 3.5; out.r = (dx * fx + dz * fz) / SQ + 3.5;
  return out;
}
/** the 8 queen directions in board space (file, rank): files/ranks first, then diagonals */
export const DIRS8: readonly (readonly [number, number])[] = [[0, 1], [1, 0], [0, -1], [-1, 0], [1, 1], [1, -1], [-1, -1], [-1, 1]];
/** board-space direction → world unit vector */
export function boardDir(b: Board, df: number, dr: number, out: { x: number; z: number }) {
  const fx = -Math.sin(b.byaw), fz = -Math.cos(b.byaw);
  const rx = Math.cos(b.byaw), rz = -Math.sin(b.byaw);
  const x = rx * df + fx * dr, z = rz * df + fz * dr, l = Math.hypot(x, z) || 1;
  out.x = x / l; out.z = z / l;
  return out;
}

// ------------------------------------------------------------------ state
export interface CourtState extends BossBase, Board {
  kind: BossKind.BlackCourt;
  /** eid = the King */
  kingMax: number;
  units: (Enemy | null)[]; // by slot (e.mode), refreshed every tick
  cellF: Int8Array; cellR: Int8Array; // where each piece stands / is moving to
  fromX: Float32Array; fromZ: Float32Array; moveT: Int16Array; moveDur: Int16Array;
  respT: Int16Array; // ticks until a fallen piece is back (0 = not pending)
  introT: number; introDone: boolean;
  tockT: number;
  act: Atk; actor: number; stage: Stage; stT: number; stDur: number; actCd: number; atkRR: number; queenCd: number;
  hitMask: number;
  lx: number; lz: number; // locked point (leap landing / charge end / beam origin)
  ldx: number; ldz: number; llen: number; // charge lane
  spinYaw: number;
  defender: number; swapT: number;
  scutT: number; scutLeft: number; sdx: number; sdz: number;
  fireRR: number;
  mate: boolean;
  toldReform: boolean;
}

export interface CourtNet extends BossNet {
  kind: BossKind.BlackCourt;
  /** eid = the King; atk = the current major action; phase = CourtBit flags */
  bx: number; bz: number; byaw: number;
  actor: number; stage: Stage; prog: number;
  lx: number; lz: number; ldir: number; llen: number;
  alive: number;
  /** the board erupting: 0..1 */
  rise: number;
  /** fallen pieces coming back: home cell + 0..1 progress */
  pending: { f: number; r: number; k: number }[];
}

const frenzy = (s: CourtState) => {
  const k = s.units[KING_SLOT];
  return k ? clamp(1 - k.hp / Math.max(1, s.kingMax), 0, 1) : 0;
};
const o2 = { x: 0, z: 0 };
const oc = { f: 0, r: 0 };

function groundAt(g: Game, x: number, z: number, y: number) {
  const t = g.world.terrain(x, z);
  return t === NO_GROUND ? y : t;
}
function targets(g: Game) {
  const out: PlayerState[] = [];
  for (const p of g.players.values()) if (isTargetable(p) && p.phase === 0) out.push(p);
  return out;
}
function nearest(g: Game, x: number, z: number, maxD = 1e9) {
  let best: PlayerState | null = null, bd = maxD;
  for (const p of targets(g)) { const d = len2(p.x - x, p.z - z); if (d < bd) { bd = d; best = p; } }
  return best;
}
function setUnit(e: Enemy, st: EState, dur: number) {
  if (e.st !== st) { e.st = st; e.stT = 0; e.stDur = dur; }
}
function faceTo(e: Enemy, x: number, z: number, rate: number) {
  const want = Math.atan2(-(x - e.x), -(z - e.z));
  const d = wrapAngle(want - e.yaw);
  e.yaw = wrapAngle(e.yaw + clamp(d, -rate * DT, rate * DT));
}
const cellKey = (f: number, r: number) => (f + 4) * 16 + (r + 4);
const inGrid = (f: number, r: number) => f >= GRID_MIN && f <= GRID_MAX && r >= GRID_MIN && r <= GRID_MAX;

// ------------------------------------------------------------------ the def
const hp = { x: 0, y: 0, z: 0, yaw: 0 };
const ph = { t: -1, vol: null as Vol | null };

export const BLACK_COURT: BossDef<CourtState> = {
  kind: BossKind.BlackCourt,
  name: 'THE BLACK COURT',
  recall: 'RECALL NOTICE: THE BLACK COURT. SIXTEEN APPLIANCES IN LIVERY, ONE COWARDLY KING. CHECKMATE IS THE ONLY EXIT.',
  types: COURT_TYPES,
  procs: true,

  spawn(g, x, z, lookX, lookZ, n, tierMul) {
    // the board faces the posse: rank 0 (the back rank) is the far side
    const byaw = Math.atan2(-(lookX - x), -(lookZ - z));
    const s: CourtState = {
      kind: BossKind.BlackCourt, eid: 0, kingMax: 1, bx: x, bz: z, byaw,
      units: new Array(SLOTS).fill(null),
      cellF: new Int8Array(SLOTS), cellR: new Int8Array(SLOTS),
      fromX: new Float32Array(SLOTS), fromZ: new Float32Array(SLOTS), moveT: new Int16Array(SLOTS), moveDur: new Int16Array(SLOTS),
      respT: new Int16Array(SLOTS),
      introT: 0, introDone: false, tockT: secs(TOCK[0]),
      act: Atk.None, actor: -1, stage: Stage.None, stT: 0, stDur: 1, actCd: secs(1.5), atkRR: 0, queenCd: secs(8),
      hitMask: 0, lx: 0, lz: 0, ldx: 0, ldz: 1, llen: 0, spinYaw: 0,
      defender: ROOK_SLOTS[0], swapT: secs(SWAP_SECS),
      scutT: secs(SCUTTLE_EVERY), scutLeft: 0, sdx: 0, sdz: 0, fireRR: 0,
      mate: false, toldReform: false,
    };
    const scale = (1 + 0.35 * (n - 1)) * tierMul;
    for (let i = 0; i < SLOTS; i++) {
      // the court rises onto its squares a rank at a time, the King last
      const e = spawnPiece(g, s, i, homeF(i), homeR(i), scale, 0.9 + (1 - homeR(i)) * 0.5 + homeF(i) * 0.06 + (i === KING_SLOT ? 0.6 : 0));
      if (i === KING_SLOT) { s.eid = e.id; s.kingMax = e.maxHp; }
    }
    return s;
  },

  step(g, s) {
    stepCourt(g, s);
  },

  raycast(g, s, r: Ray, view, maxT, out: BossHit) {
    out.t = -1; out.e = null;
    let best = maxT;
    for (const e of g.enemies) {
      if (e.dead || e.st === EState.Spawn || !isCourt(e.type)) continue;
      historyAt(e, view, g.tick, hp);
      const pc = pieceOf(e.type);
      pieceRaycast(pc, hp.x, hp.y, hp.z, hp.yaw, r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, best, ph);
      if (ph.t < 0 || !ph.vol) continue;
      best = ph.t;
      out.t = ph.t; out.e = e; out.part = ph.vol.part; out.idx = e.mode;
      out.av = ph.vol.av; out.mul = ph.vol.mul; out.weak = ph.vol.weak; out.head = ph.vol.weak;
    }
    void s;
    return out;
  },

  tookHit() { /* every piece is its own pool: nothing to forward */ },

  died(g, s, e) {
    const i = e.mode;
    s.units[i] = null;
    if (s.mate) return false;
    if (i === KING_SLOT) {
      // CHECKMATE: the court falls with its monarch
      s.mate = true;
      s.act = Atk.None; s.stage = Stage.None;
      g.emit(Ev.Boss, [e.id, CourtEv.Mate, 0, 0]);
      g.emit(Ev.Notice, [], BLACK_COURT.note!(s, CourtEv.Mate, 0, 0)!);
      for (const o of g.enemies) if (!o.dead && o !== e && isCourt(o.type)) g.killEnemy(o, -1);
      return true;
    }
    s.respT[i] = secs(RESPAWN);
    if (s.actor === i && s.stage !== Stage.None) endAction(g, s);
    return false;
  },

  note(_s, kind) {
    if (kind === CourtEv.Mate) return 'CHECKMATE. THE BLACK COURT FALLS WITH ITS KING.';
    if (kind === CourtEv.Reform) return 'THE COURT RE-FORMS ITS FALLEN. YOU CANNOT OUT-SHOOT THE COURT: FLANK THE KING.';
    return undefined;
  },

  enc(w: Writer, _g, s) {
    w.u16w(s.eid); w.cmw(s.bx); w.cmw(s.bz); w.angw(s.byaw);
    w.u8w(s.act);
    const bits = (s.introDone ? 0 : CourtBit.Intro) | (s.scutLeft > 0 ? CourtBit.Scuttle : 0) | (s.mate ? CourtBit.Mate : 0)
      | (s.defender === 0 ? CourtBit.Defender0 : s.defender === 7 ? CourtBit.Defender7 : 0);
    w.u8w(bits);
    const actor = s.actor >= 0 ? s.units[s.actor] : null;
    w.u16w(actor ? actor.id : 0); w.u8w(s.stage);
    w.u8w(Math.round(clamp(s.stage ? 1 - s.stT / Math.max(1, s.stDur) : 0, 0, 1) * 255));
    w.cmw(s.lx); w.cmw(s.lz); w.angw(Math.atan2(s.ldx, s.ldz)); w.u8w(Math.round(clamp(s.llen, 0, 25.5) * 10));
    let alive = 0;
    for (const u of s.units) if (u) alive++;
    w.u8w(alive);
    w.u8w(Math.round(clamp(s.introT / secs(1.6), 0, 1) * 255));
    let n = 0;
    for (let i = 0; i < SLOTS; i++) if (!s.units[i] && s.respT[i] > 0) n++;
    w.u8w(n);
    for (let i = 0; i < SLOTS; i++) {
      if (s.units[i] || s.respT[i] <= 0) continue;
      w.u8w(cellKey(homeF(i), homeR(i)));
      w.u8w(Math.round(clamp(1 - s.respT[i] / secs(RESPAWN), 0, 1) * 255));
    }
  },

  dec(r: Reader): CourtNet {
    const eid = r.u16(), bx = r.cm(), bz = r.cm(), byaw = r.ang(), atk = r.u8(), phase = r.u8();
    const actor = r.u16(), stage = r.u8() as Stage, prog = r.u8() / 255;
    const lx = r.cm(), lz = r.cm(), ldir = r.ang(), llen = r.u8() / 10;
    const alive = r.u8(), rise = r.u8() / 255;
    const n = r.u8();
    const pending: { f: number; r: number; k: number }[] = [];
    for (let i = 0; i < n; i++) { const c = r.u8(); pending.push({ f: Math.floor(c / 16) - 4, r: (c % 16) - 4, k: r.u8() / 255 }); }
    return { kind: BossKind.BlackCourt, eid, atk, phase, bx, bz, byaw, actor, stage, prog, lx, lz, ldir, llen, alive, rise, pending };
  },
};

export const isCourt = (t: number) => t >= EType.CourtKing && t <= EType.CourtPawn;

function spawnPiece(g: Game, s: CourtState, i: number, f: number, r: number, scale: number, rise: number) {
  cellWorld(s, f, r, o2);
  const e = g.spawnEnemy(pieceType(slotPiece(i)), o2.x, o2.z);
  e.mode = i;
  e.maxHp = e.hp = PIECE_HP[slotPiece(i)] * scale;
  e.shield = e.maxShield = 0;
  e.yaw = s.byaw;
  e.y = groundAt(g, e.x, e.z, e.y);
  e.st = EState.Spawn; e.stT = 0; e.stDur = secs(rise);
  for (let k = 0; k < e.hx.length; k++) { e.hx[k] = e.x; e.hy[k] = e.y; e.hz[k] = e.z; e.hyaw[k] = e.yaw; }
  s.units[i] = e;
  s.cellF[i] = f; s.cellR[i] = r; s.moveT[i] = 0;
  return e;
}

// ------------------------------------------------------------------ the brain
function stepCourt(g: Game, s: CourtState) {
  // refresh the roster (slot rides e.mode)
  s.units.fill(null);
  for (const e of g.enemies) if (!e.dead && isCourt(e.type)) s.units[e.mode] = e;
  for (let i = 0; i < SLOTS; i++) {
    const e = s.units[i];
    if (!e) continue;
    e.stT++;
    e.kx = e.kz = 0; // nothing shoves a chess piece off its square
    if (!stepStatus(e, g)) { s.units[i] = null; continue; }
  }
  if (s.mate || !s.units[KING_SLOT]) return;

  // ---- the board erupts, the court rises onto its squares
  s.introT++;
  if (!s.introDone) {
    let up = 0, n = 0;
    for (const e of s.units) {
      if (!e) continue;
      n++;
      if (e.st === EState.Spawn && e.stT >= e.stDur) setUnit(e, EState.Chase, 0);
      if (e.st !== EState.Spawn) up++;
    }
    if (up === n && s.introT >= secs(INTRO_SECS)) {
      s.introDone = true;
      g.emit(Ev.Boss, [s.eid, BossBeat.Intro, 1, 0]);
    }
    return;
  }

  const fz = frenzy(s);
  stepRespawns(g, s);
  stepRoles(g, s);
  stepKing(g, s, fz);
  if (s.queenCd > 0) s.queenCd--;
  if (s.actCd > 0 && s.stage === Stage.None) s.actCd--;

  // ---- the court clock
  if (--s.tockT <= 0) {
    s.tockT = secs(lerp(TOCK[0], TOCK[1], fz));
    g.emit(Ev.Boss, [s.eid, CourtEv.Tock, 0, 0]);
    tockMoves(g, s);
    if (s.stage === Stage.None && s.actCd <= 0) pickAction(g, s);
    if (s.stage === Stage.None || s.stage === Stage.Exec) pawnsFire(g, s, fz);
  }
  if (s.stage !== Stage.None) stepAction(g, s);

  // ---- every piece but the King: glide to its square, face its business
  for (let i = 0; i < SLOTS; i++) {
    const e = s.units[i];
    if (!e || i === KING_SLOT) continue;
    if (e.st === EState.Spawn) { if (e.stT >= e.stDur) setUnit(e, EState.Chase, 0); continue; }
    if (i === s.actor && s.stage !== Stage.None) continue; // the action drives it
    if (e.stunT > 0) { e.stunT--; setUnit(e, EState.Stun, e.stunT); }
    else if (e.st !== EState.Chase) setUnit(e, EState.Chase, 0);
    glide(g, s, e, i);
    const t = nearest(g, e.x, e.z, 40);
    if (t && e.stunT <= 0) faceTo(e, t.x, t.z, TURN);
  }
}

function glide(g: Game, s: CourtState, e: Enemy, i: number) {
  cellWorld(s, s.cellF[i], s.cellR[i], o2);
  let x = o2.x, z = o2.z, lift = 0;
  if (s.moveT[i] > 0) {
    const k0 = 1 - --s.moveT[i] / Math.max(1, s.moveDur[i]);
    const k = k0 * k0 * (3 - 2 * k0);
    x = lerp(s.fromX[i], o2.x, k); z = lerp(s.fromZ[i], o2.z, k);
    if (slotPiece(i) === Pc.Knight) lift = Math.sin(k0 * Math.PI) * 1.6;
  }
  e.vx = (x - e.x) / DT; e.vz = (z - e.z) / DT;
  e.x = x; e.z = z;
  e.y = groundAt(g, x, z, e.y) + lift;
  e.airborne = lift > 0.3 ? 1 : 0;
}

function startMove(s: CourtState, e: Enemy, i: number, f: number, r: number, dur: number) {
  s.fromX[i] = e.x; s.fromZ[i] = e.z;
  s.cellF[i] = f; s.cellR[i] = r;
  s.moveT[i] = s.moveDur[i] = secs(dur);
}

/** cells taken by the court (pieces where they stand or are heading, the King where he is) */
function occupancy(s: CourtState) {
  const occ = new Uint8Array(256);
  for (let i = 0; i < SLOTS; i++) {
    const e = s.units[i];
    if (!e) { if (s.respT[i] > 0 && i !== KING_SLOT) occ[cellKey(homeF(i), homeR(i))] = 1; continue; }
    if (i === KING_SLOT) {
      worldCell(s, e.x, e.z, oc);
      const f = Math.round(oc.f), r = Math.round(oc.r);
      if (inGrid(f, r)) occ[cellKey(f, r)] = 1;
      continue;
    }
    occ[cellKey(s.cellF[i], s.cellR[i])] = 1;
  }
  return occ;
}

function freeCellNear(g: Game, s: CourtState, occ: Uint8Array, f0: number, r0: number) {
  const fi = clamp(Math.round(f0), GRID_MIN, GRID_MAX), ri = clamp(Math.round(r0), GRID_MIN, GRID_MAX);
  for (let rad = 0; rad < 5; rad++) {
    for (let df = -rad; df <= rad; df++) for (let dr = -rad; dr <= rad; dr++) {
      if (Math.max(Math.abs(df), Math.abs(dr)) !== rad) continue;
      const f = fi + df, r = ri + dr;
      if (!inGrid(f, r) || occ[cellKey(f, r)]) continue;
      cellWorld(s, f, r, o2);
      if (g.world.terrain(o2.x, o2.z) === NO_GROUND) continue;
      return cellKey(f, r);
    }
  }
  return cellKey(fi, ri);
}

// candidate steps per piece (file, rank deltas)
const STEPS: number[][][] = [
  [],
  [], // queen: filled below (8 dirs × 1..2)
  [], // rook: orthogonal 1..2
  [], // bishop: diagonal 1..2
  [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]],
  [[0, 1]],
];
for (const [df, dr] of DIRS8) for (let k = 1; k <= 2; k++) {
  STEPS[Pc.Queen].push([df * k, dr * k]);
  if (df === 0 || dr === 0) STEPS[Pc.Rook].push([df * k, dr * k]); else STEPS[Pc.Bishop].push([df * k, dr * k]);
}

/** On the tock every idle piece makes one chess-shaped move toward its goal. */
function tockMoves(g: Game, s: CourtState) {
  const occ = occupancy(s);
  const king = s.units[KING_SLOT]!;
  for (let i = 0; i < SLOTS; i++) {
    const e = s.units[i];
    if (!e || i === KING_SLOT || e.st === EState.Spawn || e.stunT > 0) continue;
    if (i === s.actor && s.stage !== Stage.None) continue;
    const pc = slotPiece(i);
    const f0 = s.cellF[i], r0 = s.cellR[i];
    let gf = f0, gr = r0;
    const t = nearest(g, e.x, e.z);
    if (i === s.defender) {
      // interpose: one square-and-a-bit in front of the King, on the line to the nearest cowboy
      const kt = nearest(g, king.x, king.z);
      if (!kt) continue;
      const dx = kt.x - king.x, dz = kt.z - king.z, l = len2(dx, dz) || 1;
      worldCell(s, king.x + (dx / l) * 3.6, king.z + (dz / l) * 3.6, oc);
      gf = oc.f; gr = oc.r;
    } else if (pc === Pc.Pawn) {
      // pawns march up their file and hold on the far rank (they never leave the board)
      if (r0 >= 7) continue;
      gf = f0; gr = r0 + 1;
    } else if (t) {
      worldCell(s, t.x, t.z, oc);
      const tf = oc.f, tr = oc.r;
      const df = f0 - tf, dr = r0 - tr;
      switch (pc) {
        case Pc.Rook: // line up on the cowboy's file or rank, three or more squares off
          if (Math.abs(df) < Math.abs(dr)) { gf = tf; gr = tr + Math.sign(dr || 1) * Math.max(3, Math.min(5, Math.abs(dr))); }
          else { gr = tr; gf = tf + Math.sign(df || 1) * Math.max(3, Math.min(5, Math.abs(df))); }
          break;
        case Pc.Bishop: { // a diagonal through the cowboy, about four squares off
          const k = 4;
          gf = tf + Math.sign(df || 1) * k; gr = tr + Math.sign(dr || 1) * k;
          break;
        }
        case Pc.Knight: case Pc.Queen: { // close to striking range
          const d = Math.hypot(df, dr) || 1, want = pc === Pc.Knight ? 2.5 : 3.2;
          gf = tf + (df / d) * want; gr = tr + (dr / d) * want;
          break;
        }
      }
    } else continue;
    // pick the step that best closes on the goal
    let bf = f0, br = r0, bd = Math.hypot(gf - f0, gr - r0) - 0.01;
    for (const [sf, sr] of STEPS[pc]) {
      const f = f0 + sf, r = r0 + sr;
      if (!inGrid(f, r) || occ[cellKey(f, r)]) continue;
      // sliders can't jump over a piece (the knight can)
      if (pc !== Pc.Knight && Math.max(Math.abs(sf), Math.abs(sr)) === 2 && occ[cellKey(f0 + sf / 2, r0 + sr / 2)]) continue;
      const d = Math.hypot(gf - f, gr - r);
      if (d < bd) {
        cellWorld(s, f, r, o2);
        if (g.world.terrain(o2.x, o2.z) === NO_GROUND) continue;
        bd = d; bf = f; br = r;
      }
    }
    if (bf === f0 && br === r0) continue;
    occ[cellKey(f0, r0)] = 0; occ[cellKey(bf, br)] = 1;
    startMove(s, e, i, bf, br, pc === Pc.Knight ? HOP : MOVE);
  }
}

// ---- roles: one rook defends the King, the rooks swap duty
function stepRoles(g: Game, s: CourtState) {
  const live = ROOK_SLOTS.filter((i) => s.units[i] && s.units[i]!.st !== EState.Spawn);
  if (--s.swapT <= 0 || !live.includes(s.defender)) {
    s.swapT = secs(SWAP_SECS);
    const other = live.find((i) => i !== s.defender);
    const next = other ?? live[0] ?? -1;
    if (next !== s.defender) {
      s.defender = next;
      if (next >= 0) g.emit(Ev.Boss, [s.units[next]!.id, CourtEv.Swap, next, 0]);
    }
  }
}

// ---- respawns (G5: the home square glows, the piece rises)
function stepRespawns(g: Game, s: CourtState) {
  const scale = s.kingMax / PIECE_HP[Pc.King];
  for (let i = 0; i < SLOTS; i++) {
    if (i === KING_SLOT || s.units[i] || s.respT[i] <= 0) continue;
    if (--s.respT[i] > 0) continue;
    const occ = occupancy(s);
    occ[cellKey(homeF(i), homeR(i))] = 0;
    const c = freeCellNear(g, s, occ, homeF(i), homeR(i));
    spawnPiece(g, s, i, Math.floor(c / 16) - 4, (c % 16) - 4, scale, RISE);
    g.emit(Ev.Boss, [s.units[i]!.id, CourtEv.Reform, i, 0]);
    if (!s.toldReform) { s.toldReform = true; g.emit(Ev.Notice, [], BLACK_COURT.note!(s, CourtEv.Reform, 0, 0)!); }
  }
}

// ---- the King: always faces you, backs away, scuttles, spin-strikes
function stepKing(g: Game, s: CourtState, fz: number) {
  const k = s.units[KING_SLOT]!;
  if (s.actor === KING_SLOT && s.stage !== Stage.None) return; // the spin drives him
  if (k.stunT > 0) { k.stunT--; setUnit(k, EState.Stun, k.stunT); return; }
  if (k.st !== EState.Chase) setUnit(k, EState.Chase, 0);
  const t = nearest(g, k.x, k.z);
  k.vx = k.vz = 0;
  if (!t) { s.scutLeft = 0; return; }
  const dx = t.x - k.x, dz = t.z - k.z, d = len2(dx, dz) || 1;
  // ---- SCUTTLE: he panics, turns his back and bolts (the guaranteed window on his gold core)
  if (s.scutLeft > 0) {
    s.scutLeft--;
    faceTo(k, k.x - s.sdx, k.z - s.sdz, KING_TURN * 2.4); // face along the bolt: back to the posse
    kingStep(g, s, k, s.sdx, s.sdz, SCUTTLE_SPEED);
    return;
  }
  if (--s.scutT <= 0) {
    s.scutT = secs(lerp(SCUTTLE_EVERY, SCUTTLE_EVERY - 1.5, fz));
    s.scutLeft = secs(SCUTTLE_SECS);
    let ax = -dx / d, az = -dz / d;
    const cx = s.bx - k.x, cz = s.bz - k.z, cl = len2(cx, cz);
    if (cl > KING_R * 0.6) { ax += (cx / cl) * 0.8; az += (cz / cl) * 0.8; const l = len2(ax, az) || 1; ax /= l; az /= l; }
    s.sdx = ax; s.sdz = az;
    g.emit(Ev.Boss, [s.eid, CourtEv.Scuttle, 0, 0]);
    return;
  }
  // ---- face the nearest cowboy (finite turn: a committed strafe rounds him)
  faceTo(k, t.x, t.z, KING_TURN * (1 + 0.25 * fz));
  // ---- the spin strike, when someone closes (only when the court has no other promise out)
  if (d < SPIN_START && s.stage === Stage.None && s.actCd <= 0) { beginAction(g, s, Atk.Spin, KING_SLOT); return; }
  // ---- the coward's retreat
  if (d < PANIC) kingStep(g, s, k, -dx / d, -dz / d, KING_WALK);
}

function kingStep(g: Game, s: CourtState, k: Enemy, dx: number, dz: number, speed: number) {
  const st = speed * (k.slowT > 0 ? 0.5 : 1) * DT;
  let nx = k.x + dx * st, nz = k.z + dz * st;
  const ox = nx - s.bx, oz = nz - s.bz;
  if (len2(ox, oz) > KING_R) {
    // the edge of his domain: sidestep along it ("circle the throne")
    const l = len2(ox, oz), tx = -oz / l, tz = ox / l, sgn = tx * dx + tz * dz >= 0 ? 1 : -1;
    nx = k.x + tx * sgn * st * 0.7; nz = k.z + tz * sgn * st * 0.7;
    const l2 = len2(nx - s.bx, nz - s.bz);
    if (l2 > KING_R) { nx = s.bx + (nx - s.bx) * KING_R / l2; nz = s.bz + (nz - s.bz) * KING_R / l2; }
  }
  if (g.world.terrain(nx, nz) === NO_GROUND) return;
  k.vx = (nx - k.x) / DT; k.vz = (nz - k.z) / DT;
  k.x = nx; k.z = nz;
  k.y = groundAt(g, nx, nz, k.y);
}

// ---- major actions (G1: one at a time; G2 tell → G3 recover)
const ORDER: Pc[] = [Pc.Knight, Pc.Rook, Pc.Bishop, Pc.Queen];

function pickAction(g: Game, s: CourtState) {
  for (let n = 0; n < ORDER.length; n++) {
    const pc = ORDER[(s.atkRR + n) % ORDER.length];
    for (let i = 0; i < SLOTS; i++) {
      const e = s.units[i];
      if (!e || slotPiece(i) !== pc || e.st === EState.Spawn || e.stunT > 0 || i === s.defender || s.moveT[i] > 0) continue;
      const t = nearest(g, e.x, e.z);
      if (!t) continue;
      const dx = t.x - e.x, dz = t.z - e.z, d = len2(dx, dz);
      let ok = false;
      switch (pc) {
        case Pc.Knight: ok = d > 4 && d < 16; break;
        case Pc.Queen: ok = d < 16 && s.queenCd <= 0; break;
        case Pc.Rook: case Pc.Bishop: {
          worldCell(s, t.x, t.z, oc);
          const df = oc.f - s.cellF[i], dr = oc.r - s.cellR[i];
          ok = d < CHARGE_MAX && (pc === Pc.Rook ? Math.min(Math.abs(df), Math.abs(dr)) < 0.55 : Math.abs(Math.abs(df) - Math.abs(dr)) < 0.6);
          break;
        }
      }
      if (!ok) continue;
      s.atkRR = (s.atkRR + n + 1) % ORDER.length;
      beginAction(g, s, pc === Pc.Knight ? Atk.Leap : pc === Pc.Rook ? Atk.Charge : pc === Pc.Bishop ? Atk.Mitre : Atk.Decree, i, t);
      return;
    }
  }
}

function beginAction(g: Game, s: CourtState, atk: Atk, slot: number, t?: PlayerState) {
  const e = s.units[slot]!;
  s.act = atk; s.actor = slot; s.stage = Stage.Tell;
  s.stT = s.stDur = secs(ATK_SECS[atk][0]);
  s.hitMask = 0;
  s.moveT[slot] = 0;
  setUnit(e, EState.Windup, s.stDur);
  // the impact point / lane is LOCKED at the tell (G2)
  switch (atk) {
    case Atk.Charge: {
      worldCell(s, t!.x, t!.z, oc);
      const df = oc.f - s.cellF[slot], dr = oc.r - s.cellR[slot];
      const along = Math.abs(df) > Math.abs(dr) ? [Math.sign(df), 0] : [0, Math.sign(dr) || 1];
      boardDir(s, along[0], along[1], o2);
      s.ldx = o2.x; s.ldz = o2.z;
      // run to the far edge of the grid along the lane (or CHARGE_MAX)
      let len = 0;
      for (let k = 1; k <= 6; k++) { const f = s.cellF[slot] + along[0] * k, r = s.cellR[slot] + along[1] * k; if (!inGrid(f, r)) break; len = k * SQ; }
      s.llen = Math.min(CHARGE_MAX, len);
      s.lx = e.x; s.lz = e.z;
      break;
    }
    case Atk.Mitre: case Atk.Decree:
      s.lx = e.x; s.lz = e.z; s.llen = atk === Atk.Mitre ? MITRE_LEN : DECREE_LEN;
      boardDir(s, 0, 1, o2); s.ldx = o2.x; s.ldz = o2.z;
      if (atk === Atk.Decree) s.queenCd = secs(QUEEN_CD);
      break;
    case Atk.Leap: {
      // the landing never leaves the court's grid
      worldCell(s, t!.x, t!.z, oc);
      cellWorld(s, clamp(oc.f, GRID_MIN - 0.4, GRID_MAX + 0.4), clamp(oc.r, GRID_MIN - 0.4, GRID_MAX + 0.4), o2);
      let lx = o2.x, lz = o2.z;
      if (g.world.terrain(lx, lz) === NO_GROUND) { lx = e.x; lz = e.z; }
      s.lx = lx; s.lz = lz; s.llen = 0;
      s.fromX[slot] = e.x; s.fromZ[slot] = e.z;
      break;
    }
    case Atk.Spin:
      s.spinYaw = e.yaw; s.lx = e.x; s.lz = e.z; s.llen = SPIN_R;
      break;
  }
  g.emit(Ev.Boss, [e.id, BossBeat.Attack, atk, slot]);
}

function endAction(g: Game, s: CourtState) {
  const i = s.actor, e = i >= 0 ? s.units[i] : null;
  if (e && i !== KING_SLOT) {
    // settle on the nearest free square
    const occ = occupancy(s);
    occ[cellKey(s.cellF[i], s.cellR[i])] = 0;
    worldCell(s, e.x, e.z, oc);
    const c = freeCellNear(g, s, occ, oc.f, oc.r);
    startMove(s, e, i, Math.floor(c / 16) - 4, (c % 16) - 4, MOVE);
    setUnit(e, EState.Chase, 0);
  }
  if (e && i === KING_SLOT) setUnit(e, EState.Chase, 0);
  s.act = Atk.None; s.actor = -1; s.stage = Stage.None;
  s.actCd = secs(lerp(1.4, 0.6, frenzy(s))); // the mandatory movement beat before the next promise
}

function stepAction(g: Game, s: CourtState) {
  const e = s.actor >= 0 ? s.units[s.actor] : null;
  if (!e) { endAction(g, s); return; }
  e.stunT = 0; // a piece mid-promise doesn't flinch
  const dur = s.stDur, k = 1 - s.stT / Math.max(1, dur);
  const atk = s.act;
  switch (s.stage) {
    case Stage.Tell:
      if (atk === Atk.Spin) e.yaw = wrapAngle(s.spinYaw + (Math.PI / 4) * Math.min(1, k * 1.6)); // the cock-back
      else if (atk === Atk.Charge) faceTo(e, e.x + s.ldx, e.z + s.ldz, TURN * 2);
      else if (atk === Atk.Leap) faceTo(e, s.lx, s.lz, TURN * 2);
      e.y = groundAt(g, e.x, e.z, e.y);
      break;
    case Stage.Exec:
      execute(g, s, e, k);
      break;
    case Stage.Recover:
      e.y = groundAt(g, e.x, e.z, e.y);
      break;
  }
  if (--s.stT > 0) return;
  if (s.stage === Stage.Tell) {
    s.stage = Stage.Exec; s.stT = s.stDur = secs(atk === Atk.Charge ? Math.max(0.2, s.llen / CHARGE_SPEED) : ATK_SECS[atk][1]);
    setUnit(e, EState.Attack, s.stDur);
    if (atk === Atk.Spin) spinHit(g, s, e);
    if (atk === Atk.Decree || atk === Atk.Mitre) g.emit(Ev.Boss, [e.id, CourtEv.Impact, atk, 0]);
  } else if (s.stage === Stage.Exec) {
    if (atk === Atk.Leap) { e.x = s.lx; e.z = s.lz; e.y = groundAt(g, e.x, e.z, e.y); e.airborne = 0; leapHit(g, s, e); }
    if (atk === Atk.Spin) e.yaw = wrapAngle(s.spinYaw + Math.PI / 4 - Math.PI * 1.25);
    s.stage = Stage.Recover; s.stT = s.stDur = secs(ATK_SECS[atk][2]);
    setUnit(e, EState.Recover, s.stDur);
  } else endAction(g, s);
}

function execute(g: Game, s: CourtState, e: Enemy, k: number) {
  switch (s.act) {
    case Atk.Charge: {
      const d = s.llen * k;
      const nx = s.lx + s.ldx * d, nz = s.lz + s.ldz * d;
      e.vx = (nx - e.x) / DT; e.vz = (nz - e.z) / DT;
      e.x = nx; e.z = nz; e.y = groundAt(g, nx, nz, e.y);
      for (const p of targets(g)) {
        const dx = p.x - e.x, dz = p.z - e.z;
        const along = dx * s.ldx + dz * s.ldz, side = dx * -s.ldz + dz * s.ldx;
        if (Math.abs(along) > 1.4 || Math.abs(side) > CHARGE_W || Math.abs(p.y - e.y) > 2.6) continue;
        // knocked aside, out of the lane
        const sg = side >= 0 ? 1 : -1;
        hit(g, s, p, CHARGE_DMG, e.x, e.z, -s.ldz * sg * KNOCK + s.ldx * 6, s.ldx * sg * KNOCK + s.ldz * 6);
      }
      break;
    }
    case Atk.Mitre: lineHits(g, s, e, true, false, MITRE_LEN, MITRE_W, MITRE_DMG); break;
    case Atk.Decree: lineHits(g, s, e, true, true, DECREE_LEN, DECREE_W, DECREE_DMG); break;
    case Atk.Leap: {
      const q = k * k * (3 - 2 * k);
      e.x = lerp(s.fromX[s.actor], s.lx, q); e.z = lerp(s.fromZ[s.actor], s.lz, q);
      e.y = groundAt(g, e.x, e.z, e.y) + Math.sin(k * Math.PI) * LEAP_H;
      e.airborne = 1;
      break;
    }
    case Atk.Spin:
      e.yaw = wrapAngle(s.spinYaw + Math.PI / 4 - Math.PI * 1.25 * k); // the whip
      break;
  }
}

/** one hit per cowboy per action (G4), shoved inboard near the rim */
function hit(g: Game, s: CourtState, p: PlayerState, dmg: number, fx: number, fz: number, kx: number, kz: number) {
  const bit = 1 << (p.id & 31);
  if (s.hitMask & bit) return;
  s.hitMask |= bit;
  const hx = s.bx - p.x, hz = s.bz - p.z;
  if (len2(hx, hz) > 22 && kx * hx + kz * hz < 0) { kx = -kx; kz = -kz; }
  g.hurtPlayer(p, dmg, fx, fz, 0, kx, kz);
}

/** beams along the bishop's diagonals (diag) and, for the queen, her files and ranks too (orth) */
function lineHits(g: Game, s: CourtState, e: Enemy, diag: boolean, orth: boolean, len: number, w: number, dmg: number) {
  for (const p of targets(g)) {
    if (Math.abs(p.y - e.y) > 3.5) continue;
    const px = p.x - s.lx, pz = p.z - s.lz;
    for (let d = 0; d < 8; d++) {
      const isDiag = d >= 4;
      if ((isDiag && !diag) || (!isDiag && !orth)) continue;
      boardDir(s, DIRS8[d][0], DIRS8[d][1], o2);
      const along = px * o2.x + pz * o2.z, side = Math.abs(px * -o2.z + pz * o2.x);
      if (along < 0.6 || along > len || side > w) continue;
      hit(g, s, p, dmg, s.lx, s.lz, o2.x * KNOCK, o2.z * KNOCK);
      break;
    }
  }
}

function leapHit(g: Game, s: CourtState, e: Enemy) {
  for (const p of targets(g)) {
    const dx = p.x - e.x, dz = p.z - e.z, d = len2(dx, dz);
    if (d > LEAP_R || Math.abs(p.y - e.y) > 3) continue;
    hit(g, s, p, LEAP_DMG, e.x, e.z, (dx / (d || 1)) * KNOCK, (dz / (d || 1)) * KNOCK);
  }
  g.emit(Ev.Boss, [e.id, CourtEv.Impact, Atk.Leap, 0]);
}

function spinHit(g: Game, s: CourtState, e: Enemy) {
  for (const p of targets(g)) {
    const dx = p.x - e.x, dz = p.z - e.z, d = len2(dx, dz);
    if (d > SPIN_R || Math.abs(p.y - e.y) > 3) continue;
    hit(g, s, p, SPIN_DMG, e.x, e.z, (dx / (d || 1)) * 20, (dz / (d || 1)) * 20);
  }
  g.emit(Ev.Boss, [e.id, CourtEv.Impact, Atk.Spin, 0]);
}

/** tests / lab: start a major action now (the brain picks its own in play) */
export function courtForce(g: Game, s: CourtState, atk: Atk, slot: number) {
  const e = s.units[slot];
  if (!e || s.stage !== Stage.None) return false;
  beginAction(g, s, atk, slot, nearest(g, e.x, e.z) ?? undefined);
  return true;
}

// ---- pawns: minor pressure on the tock (held during every tell / recover)
function pawnsFire(g: Game, s: CourtState, fz: number) {
  let shots = 2 + Math.round(fz * 2);
  for (let k = 0; k < 8 && shots > 0; k++) {
    const i = 8 + ((s.fireRR + k) % 8);
    const e = s.units[i];
    if (!e || e.st !== EState.Chase || e.stunT > 0) continue;
    const t = nearest(g, e.x, e.z, 24);
    if (!t) continue;
    const fx = -Math.sin(e.yaw), fzz = -Math.cos(e.yaw);
    const dx = t.x - e.x, dz = t.z - e.z, d = len2(dx, dz) || 1;
    if ((dx * fx + dz * fzz) / d < 0.5) continue;
    const ox = e.x + fx * 0.75, oy = e.y + 1.24, oz = e.z + fzz * 0.75;
    const ax = t.x - ox, ay = t.y + 1.1 - oy, az = t.z - oz, l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
    const sp = BULLETS[BKind.Pellet].speed;
    g.fireBullet(BKind.Pellet, ox, oy, oz, (ax / l) * sp, (ay / l) * sp, (az / l) * sp, e.id);
    shots--;
    s.fireRR = (s.fireRR + k + 1) % 8;
  }
}
